<template>
  <div class="chess-calendar">
    <div class="chess-calendar__header">
      <e-btn
        class="chess-calendar__button"
        icon
        variant="ghost"
        :disabled="isPrevMonthButtonDisabled"
        @click="handlePrevClick"
      >
        <e-icon name="chevronLeft" size="12" />
      </e-btn>
      <div class="chess-calendar__month-year" @click="setIsMonthsVisible(!isMonthsVisible)">
        {{ MONTH_NAMES[currentMonth] }}, {{ currentYear }}
      </div>
      <e-btn
        class="chess-calendar__button"
        icon
        variant="ghost"
        :disabled="isNextMonthButtonDisabled"
        @click="handleNextClick"
      >
        <e-icon name="chevronRight" size="12" />
      </e-btn>
    </div>

    <div v-if="isMonthsVisible" class="chess-calendar__months">
      <div
        v-for="item in calendarMonths"
        :key="item.date"
        class="chess-calendar__month"
        :class="{
          'chess-calendar__month--current': item.month === nowMonth && item.year === nowYear,
          'chess-calendar__month--selected': item.month === currentMonth && item.year === currentYear,
        }"
        @click="handleMonthClick(item.month, item.year)"
      >
        {{ item.date }}
      </div>
    </div>

    <template v-else>
      <ul class="chess-calendar__weekdays">
        <li>Пн</li>
        <li>Вт</li>
        <li>Ср</li>
        <li>Чт</li>
        <li>Пт</li>
        <li>Сб</li>
        <li>Вс</li>
      </ul>

      <ul class="chess-calendar__days">
        <li
          v-for="(day, dayIndex) in calendarDays"
          :key="day.id"
          :style="{ height: `${props.table.cellHeight || DEFAULT_CELL_HEIGHT}px` }"
        >
          <chess-date-cell
            ref="cellsRef"
            :id="day.id"
            :period="props.period"
            :date="day.date"
            :value="props.cells[day.date]"
            :range="!!props.cells[day.date]?.rangeId"
            :first-in-range="props.cells[calendarDays[dayIndex - 1]?.date]?.rangeId !== props.cells[day.date]?.rangeId"
            :last-in-range="props.cells[calendarDays[dayIndex + 1]?.date]?.rangeId !== props.cells[day.date]?.rangeId"
            :range-inactive="rangeInactive(day.date)"
            :disabled="!props.isEdit"
            :variant="props.cells[day.date]?.variant"
            day-visible
            day-position="right"
            :chess-dates-available-from="props.chessDatesAvailableFrom"
            @click="handleCellClick(day.id)"
            @select="emit('select', $event ? [$event] : [])"
          />
        </li>
      </ul>
    </template>
  </div>
</template>

<script setup lang="ts" generic="T">
import dayjs from 'dayjs';
import { computed, ref, useTemplateRef, watch } from 'vue';
import type {
  ChessDateCellType,
  ChessTableRowCellsType,
  ChessTableType,
} from '@/core/components/interface/Chess/Chess.type';
import type { RangeOfDateOnly } from '@/graphql/generated';
import { DEFAULT_CELL_HEIGHT } from '@/core/components/interface/Chess/Chess.const';
import ChessDateCell from '@/core/components/interface/Chess/ChessDateCell.vue';
import { EBtn, EIcon } from '@/core/components/ui';
import useToggle from '@/core/composables/useToggle';

const props = defineProps<{
  period?: RangeOfDateOnly;
  table: ChessTableType<T>;
  cells: ChessTableRowCellsType<T>;
  isEdit?: boolean;
  chessDatesAvailableFrom?: string; // Date in format YYYY-MM-DD
}>();

const emit = defineEmits<{
  (event: 'select', data: ChessDateCellType<T>[]): void;
}>();

const MONTH_NAMES = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

const cellsRef = useTemplateRef('cellsRef');

const [isMonthsVisible, setIsMonthsVisible] = useToggle();

const nowDate = new Date();
const nowMonth = nowDate.getMonth();
const nowYear = nowDate.getFullYear();

const currentMonth = ref<number>(nowMonth);
const currentYear = ref<number>(nowYear);
const prevMonth = ref<number>(nowMonth);
const prevYear = ref<number>(nowYear);
const nextMonth = ref<number>(nowMonth);
const nextYear = ref<number>(nowYear);

const rangeInactive = (dayDate: string) => {
  const date = Object.values(props.cells).findLast((item) => item.rangeId === props.cells[dayDate]?.rangeId)?.date;

  return !!date && date < dayjs(Date.now()).format('YYYY-MM-DD');
};

const prevMonthDays = computed(() => {
  const monthDays = new Date(currentYear.value, currentMonth.value, 0).getDate();
  const days = new Date(currentYear.value, currentMonth.value, 0).getDay();

  const result = [];

  for (let i = days; i > 0; i--) {
    result.push(monthDays - i + 1);
  }

  return result;
});

const currentMonthDays = computed(() => {
  const days = new Date(currentYear.value, currentMonth.value + 1, 0).getDate();

  return Array.from({ length: days }).map((_, index) => index + 1);
});

const nextMonthDays = computed(() => {
  const days = (7 - new Date(currentYear.value, currentMonth.value + 1, 1).getDay() + 1) % 7;

  return Array.from({ length: days }).map((_, index) => index + 1);
});

const calendarDays = computed(() => {
  return [
    ...prevMonthDays.value.map((day) => {
      const date = getFormattedDate(prevYear.value, prevMonth.value, day);
      return { date, id: `cell-${date}` };
    }),
    ...currentMonthDays.value.map((day) => {
      const date = getFormattedDate(currentYear.value, currentMonth.value, day);
      return { date, id: `cell-${date}` };
    }),
    ...nextMonthDays.value.map((day) => {
      const date = getFormattedDate(nextYear.value, nextMonth.value, day);
      return { date, id: `cell-${date}` };
    }),
  ];
});

const calendarMonths = computed(() => {
  if (!props.period) {
    return [];
  }

  const result = [];

  let current = dayjs(props.period.from).startOf('month');
  const end = dayjs(props.period.to).endOf('month');
  const today = dayjs().startOf('month');

  while (current.isBefore(end) || current.isSame(end, 'month')) {
    if (current.isSame(today) || current.isAfter(today)) {
      const label = current.format('MMM, YY');

      result.push({
        date: label.charAt(0).toUpperCase() + label.slice(1),
        month: current.month(), // число от 0 до 11
        year: current.year(),
      });
    }
    current = current.add(1, 'month');
  }

  return result;
});

const isPrevMonthButtonDisabled = computed(() => {
  if (!props.period) {
    return true;
  }

  return dayjs(new Date(currentYear.value, currentMonth.value, -1)).format('YYYY-MM-DD') < props.period.from;
});

const isNextMonthButtonDisabled = computed(() => {
  if (!props.period) {
    return true;
  }

  return dayjs(new Date(nextYear.value, nextMonth.value)).format('YYYY-MM-DD') > props.period.to;
});

watch(
  () => currentMonth.value,
  () => {
    updatePrevNextMonthYear();
  },
  { immediate: true },
);

const handleCellClick = (cellId: string) => {
  if (!Array.isArray(cellsRef.value)) return;

  const cell = cellsRef.value.find((item) => item.$el.id === cellId);
  cell.selectCell();
};

const handleMonthClick = (month: number, year: number) => {
  currentMonth.value = month;
  currentYear.value = year;
  setIsMonthsVisible(false);
};

const getFormattedDate = (year: number, month: number, day: number) => {
  return dayjs(new Date(year, month, day)).format('YYYY-MM-DD');
};

const handlePrevClick = () => {
  const curDate = new Date(currentYear.value, currentMonth.value);

  curDate.setMonth(curDate.getMonth() - 1);

  currentMonth.value = curDate.getMonth();
  currentYear.value = curDate.getFullYear();
};

const handleNextClick = () => {
  const curDate = new Date(currentYear.value, currentMonth.value);

  curDate.setMonth(curDate.getMonth() + 1);

  currentMonth.value = curDate.getMonth();
  currentYear.value = curDate.getFullYear();
};

const scrollTo = (date: string) => {
  const targetDate = new Date(date);

  currentMonth.value = targetDate.getMonth();
  currentYear.value = targetDate.getFullYear();
};

defineExpose({
  scrollTo,
});

function updatePrevNextMonthYear() {
  const curDate = new Date(currentYear.value, currentMonth.value);

  curDate.setMonth(curDate.getMonth() - 1);

  prevMonth.value = curDate.getMonth();
  prevYear.value = curDate.getFullYear();

  curDate.setMonth(curDate.getMonth() + 2);

  nextMonth.value = curDate.getMonth();
  nextYear.value = curDate.getFullYear();
}
</script>

<style scoped lang="scss">
.chess-calendar {
  @include xs {
    margin: 0 -12px;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    margin-bottom: 8px;

    @include xs {
      margin-bottom: 4px;
    }
  }

  &__month-year {
    font-size: 14px;
    font-weight: 600;
    color: $text-link-color;
  }

  &__button {
    --v-btn-height: 12px !important;
  }

  &__weekdays {
    display: grid;

    grid-gap: 8px;
    grid-template-columns: repeat(7, minmax(0, 1fr));

    margin-bottom: 8px;

    @include xs {
      margin-bottom: 4px;
    }

    li {
      display: flex;
      justify-content: center;
      align-items: center;

      height: 40px;

      font-size: 10px;
      font-weight: 600;
      color: $text-secondary-color;
      text-transform: uppercase;
    }
  }

  &__days {
    display: grid;

    grid-gap: 8px;
    grid-template-columns: repeat(7, minmax(0, 1fr));

    @include xs {
      grid-gap: 4px;
    }

    li {
      display: block;

      border-radius: 8px;

      @include xs {
        border-radius: 4px;
      }
    }
  }

  &__months {
    display: grid;

    grid-gap: 4px;
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  &__month {
    display: flex;
    justify-content: center;
    align-items: center;

    height: 48px;

    font-size: 12px;
    font-weight: 600;
    color: $text-secondary-color;

    border-radius: 10px;

    &--selected {
      color: $text-primary-color;

      background-color: $primary-light-color;
    }

    &--current {
      color: $text-link-color;

      border: 1px solid currentColor;
    }
  }
}
</style>
